import { useCalculateAgendaRecordMutation } from 'store/services/flow/agendaRecords';
import type { MutableRefObject } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  buildCalculateAttributes,
  fixAttributeRecordIds,
  mapCalculateResponseToFormValues,
  NEW_RECORD_PREFIX,
} from 'utils/agendaRecord/agendaRecord.ts';
import { calculateResponseSchema } from 'utils/validations/useCalculateResponseSchema';
import type { VisibilityResultWithName } from 'utils/validations/multipleDefinitions';
import { getFormFieldPath } from 'components/common/Table/editableComponentFactory';
import type { AttributeType, InlineRecordType, UserAgendaTableDataSchemaType } from 'types';
import type { TableRef } from 'components/common/Table/Table.tsx';
import { VISIBILITY_STATE } from 'types/enums';

interface UseAgendaTableCalculationProps {
  dataSchema: UserAgendaTableDataSchemaType;
}

export const useAgendaTableCalculation = ({ dataSchema }: UseAgendaTableCalculationProps) => {
  const { t } = useTranslation();
  const [calculateAgendaRecord] = useCalculateAgendaRecordMutation();

  const [calculatedValues, setCalculatedValues] = useState<Record<string, unknown>>();
  const [calculatedVisibility, setCalculatedVisibility] = useState<Record<string, VisibilityResultWithName>>();

  const isAttributeEditableAndVisible = useCallback(
    (rowId: string, formItemId: string): boolean => {
      const schemaItem = dataSchema[formItemId];
      if (!schemaItem) return false;

      if (!schemaItem.isEditable) return false;

      const visibilityKey = getFormFieldPath(rowId, formItemId);
      const visibility = calculatedVisibility?.[visibilityKey];
      const visibilityState = visibility?.visibilityState;

      if (visibilityState === VISIBILITY_STATE.HIDDEN) return false;

      return true;
    },
    [dataSchema, calculatedVisibility]
  );

  const handleCalculateRow = useCallback(
    async (
      tableRef: MutableRefObject<TableRef | null>,
      calculatedAgendaId: string,
      rowId: string,
      rowData: AttributeType[],
      inlineRecordData?: InlineRecordType[],
      persistedRecordId?: string
    ) => {
      const hasRowData = Boolean(rowData?.length);
      const hasInlineData = Boolean(inlineRecordData?.some((record) => record.attributes.length));

      if (!hasRowData && !hasInlineData) {
        toast.error(t('flow.modulesAndAgendas.form.item.records.noDataToCalculate'));
        return;
      }

      try {
        const filteredRowData = (rowData ?? []).filter((attr) => isAttributeEditableAndVisible(rowId, attr.formItemId));
        const attributes = buildCalculateAttributes(filteredRowData);
        const inline = fixAttributeRecordIds(
          (inlineRecordData ?? [])
            .map((record) => ({
              ...record,
              attributes: buildCalculateAttributes(
                record.attributes.filter((attr) => isAttributeEditableAndVisible(rowId, attr.formItemId))
              ) as AttributeType[],
            }))
            .filter((record) => record.attributes.length > 0)
        );
        const recordId = rowId.startsWith(NEW_RECORD_PREFIX) ? null : persistedRecordId || rowId;

        const res = await calculateAgendaRecord({
          agendaId: calculatedAgendaId,
          body: {
            recordId,
            attributes,
            ...(inline.length ? { inlineRecordData: inline } : {}),
          },
        });

        if ('data' in res) {
          const validatedData = await calculateResponseSchema.validate(res.data);
          const updatedValues = mapCalculateResponseToFormValues(validatedData.attributes);

          if (!dataSchema || Object.keys(dataSchema).length === 0) return;

          const respondedFormItemIds = new Set(
            validatedData.attributes.map((attribute) => attribute.formItem?.id).filter((id): id is string => Boolean(id))
          );

          const mappedRowValues = Object.fromEntries(
            Object.keys(dataSchema)
              .filter((formItemId) => respondedFormItemIds.has(formItemId))
              .map((formItemId) => [formItemId, updatedValues[formItemId]])
          );

          const removedFormItemIds = Object.keys(dataSchema).filter(
            (formItemId) => !respondedFormItemIds.has(formItemId) && !dataSchema[formItemId]?.joinedColumn
          );

          setCalculatedValues((prev) => ({
            ...prev,
            [rowId]: mappedRowValues,
          }));

          setCalculatedVisibility((prev) => {
            const next = Object.fromEntries(Object.entries(prev ?? {}).filter(([path]) => !path.startsWith(`${rowId}.`)));

            // Add visibility for responded attributes
            validatedData.attributes.forEach((attribute) => {
              const formItemId = attribute.formItem?.id;
              if (!formItemId || !(formItemId in dataSchema) || !attribute.visibility) return;

              next[getFormFieldPath(rowId, formItemId)] = {
                ...attribute.visibility,
                ...(attribute.formItem?.name ? { formItemName: attribute.formItem.name } : {}),
              };
            });

            // Explicitly mark removed fields as HIDDEN to override base visibility
            removedFormItemIds.forEach((formItemId) => {
              next[getFormFieldPath(rowId, formItemId)] = {
                visibilityState: VISIBILITY_STATE.HIDDEN,
                formItemUiDefinition: null,
                multipleUiDefinitions: false,
              };
            });

            return next;
          });

          const currentRow = tableRef.current?.getValues?.()?.[rowId] ?? {};
          const preservedJoinedValues = Object.fromEntries(
            Object.entries(currentRow).filter(([formItemId]) => dataSchema[formItemId]?.joinedColumn && !respondedFormItemIds.has(formItemId))
          );

          tableRef.current?.updateFormDataRow(rowId, { ...preservedJoinedValues, ...mappedRowValues });
        }
      } catch {
        toast.error(t('toast.calculated.error'));
      }
    },
    [calculateAgendaRecord, dataSchema, isAttributeEditableAndVisible, t]
  );

  const clearCalculatedValues = useCallback(() => {
    setCalculatedValues(undefined);
    setCalculatedVisibility(undefined);
  }, []);

  return {
    handleCalculateRow,
    calculatedValues,
    calculatedVisibility,
    setCalculatedValues,
    clearCalculatedValues,
  };
};
